import { ArrowLeft, RefreshCw } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAssessmentFixtureEvalSummary } from "@/hooks/admin/assessmentFixtures";
import { AdminLayout } from "@/layouts/AdminLayout";
import type { CriterionEvalStats } from "@/types";

function fmt(n: number | null | undefined, digits = 2) {
  if (n === null || n === undefined) return "—";
  return n.toFixed(digits);
}

function pct(n: number) {
  return `${(n * 100).toFixed(0)}%`;
}

function CriterionCard({ stat }: { stat: CriterionEvalStats }) {
  const stdevBadge =
    stat.score_stdev === null
      ? "bg-gray-100 text-gray-600"
      : stat.score_stdev < 2
        ? "bg-green-100 text-green-700"
        : stat.score_stdev < 5
          ? "bg-yellow-100 text-yellow-700"
          : "bg-red-100 text-red-700";

  const lowDetection = Object.entries(stat.component_detection_rate).filter(([, rate]) => rate < 1);

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-semibold text-gray-900">{stat.criterion_name}</h3>
        <span className="text-xs text-gray-500">
          {stat.completed} completed / {stat.runs} total
          {stat.errored > 0 && ` (${stat.errored} errored)`}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-3 text-sm mb-3">
        <div>
          <div className="text-xs text-gray-500">Mean</div>
          <div className="font-medium text-gray-900">{fmt(stat.score_mean)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Stdev</div>
          <div className={`inline-block px-1.5 py-0.5 rounded font-medium ${stdevBadge}`}>
            {fmt(stat.score_stdev)}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Min</div>
          <div className="font-medium text-gray-900">{stat.score_min ?? "—"}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Max</div>
          <div className="font-medium text-gray-900">{stat.score_max ?? "—"}</div>
        </div>
      </div>

      {Object.keys(stat.cap_firing_rate).length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-1">Cap-firing rate</div>
          <ul className="text-xs space-y-0.5">
            {Object.entries(stat.cap_firing_rate).map(([reason, rate]) => (
              <li key={reason} className="text-gray-700">
                <span className="font-medium">{pct(rate)}</span>{" "}
                <span className="text-gray-500">{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {lowDetection.length > 0 && (
        <div>
          <div className="text-xs text-gray-500 mb-1">
            Components missed (detection rate &lt; 100%)
          </div>
          <div className="flex flex-wrap gap-1">
            {lowDetection.map(([code, rate]) => (
              <span
                key={code}
                className={`text-xs px-1.5 py-0.5 rounded ${
                  rate < 0.5
                    ? "bg-red-50 text-red-700"
                    : rate < 0.8
                      ? "bg-yellow-50 text-yellow-700"
                      : "bg-gray-100 text-gray-700"
                }`}
                title={`Detected in ${pct(rate)} of completed runs`}
              >
                {code} {pct(rate)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminAssessmentFixtureEvalPage() {
  const { fixtureId } = useParams<{ fixtureId: string }>();
  const navigate = useNavigate();
  const summaryQuery = useAssessmentFixtureEvalSummary(fixtureId);
  const summary = summaryQuery.data ?? null;
  const loading = summaryQuery.isPending;
  const error = summaryQuery.isError
    ? summaryQuery.error instanceof Error
      ? summaryQuery.error.message
      : "Failed to load eval summary"
    : null;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(`/admin/assessment-fixtures/${fixtureId}`)}
          className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to fixture
        </button>

        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-white">Eval summary</h1>
          <button
            type="button"
            onClick={() => summaryQuery.refetch()}
            disabled={summaryQuery.isFetching}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-white border text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {summary && (
          <>
            <div className="mb-4 text-sm text-white/70">
              Sample size per criterion: {summary.sample_size_per_criterion}. Aggregates only
              single-criterion runs (criterion_key set).
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {summary.criteria.map((s) => (
                <CriterionCard key={s.criterion_key} stat={s} />
              ))}
            </div>
          </>
        )}

        {!summary && !error && !loading && (
          <p className="text-sm text-white/70">
            No eval data yet. Trigger an eval sweep from the fixture page.
          </p>
        )}
      </div>
    </AdminLayout>
  );
}
